import React, { useCallback, useEffect, type SyntheticEvent } from 'react'
import { useMediaQuery } from 'react-responsive'
import { Box, Heading, Flex, theme, Relative, Button } from 'pcln-design-system'
import {
  UpcomingHotel,
  UpcomingFlight,
  UpcomingRentalCar,
  CrossSellCard
} from '@pcln/trip-activity-components'
import useDetectBrowser from '@/hooks/useDetectBrowser'

import {
  fireViewPromotionClick,
  fireViewPromotionXsell,
  fireViewTripClick,
  getGA4ProductTypeById
} from './ga4'
import { Booking, ProductType, HotelBooking, FlightBooking } from './types'
import type {
  BookedItemApiResponse,
  BookedHotelApiResponse
} from '../TripGrouping/types'
import {
  isTripGroupingBooking,
  isStayBooking,
  isFlyBooking,
  isDriveBooking
} from '../TripGrouping/utils'

function ViewMyTrips() {
  return (
    <Button
      variation="subtle"
      size="small"
      onClick={() => {
        fireViewTripClick()
        window.open('/next-profile/trips')
      }}
      mb={[2, null, null, 0]}
    >
      View My Trips
    </Button>
  )
}

function isHotelBookingDefault(payload: Booking): payload is HotelBooking {
  return payload.productId === 5
}

function isFlyBookingDefault(payload: Booking): payload is FlightBooking {
  return payload.productId === 1
}

function getOriginAndDestination(booking: Booking | BookedItemApiResponse) {
  // experiment (Trip Grouping API Response)
  if (isTripGroupingBooking(booking)) {
    if (isStayBooking(booking)) {
      return {
        origin: '',
        destination: `${booking.cityName}, ${booking.stateCode}`
      }
    }
    if (isFlyBooking(booking)) {
      return {
        origin: booking.originAirportCode,
        destination: booking.destinationAirportCode
      }
    }
    if (isDriveBooking(booking)) {
      return {
        origin: '',
        destination: booking.pickupLocationCityName
      }
    }
  }

  // default (profile Ql response)
  // flight
  if (isFlyBookingDefault(booking)) {
    return {
      origin: booking.originLocationCode,
      destination: booking.destinationLocationCode
    }
  }
  // hotel
  if (isHotelBookingDefault(booking)) {
    return {
      origin: '',
      destination: `${booking.destinationCity}${
        booking.destinationCountryCode
          ? `, ${booking.destinationCountryCode}`
          : ''
      }`
    }
  }
  // rc
  if (booking.productId === 8) {
    return {
      origin: '',
      destination: booking.destinationCity
    }
  }

  return { origin: '', destination: '' }
}

function UpcomingTripCard({
  booking
}: {
  booking: Booking | BookedItemApiResponse
}) {
  const itineraryUrl = `/travel-itinerary/?offerToken=${booking.offerToken}`

  const handleUpcomingTripOnClick = (bookingProductId: 1 | 5 | 8) => {
    if (typeof window !== 'undefined') {
      window.open(itineraryUrl)
    }
    fireViewPromotionClick(
      booking.productId,
      getGA4ProductTypeById(bookingProductId),
      booking.offerId
    )
  }

  const { origin, destination } = getOriginAndDestination(booking)

  const { travelEndDateTime, travelStartDateTime } = booking

  if (booking.productId === 1) {
    const isOneWay = travelStartDateTime === travelEndDateTime
    return (
      <UpcomingFlight
        origin={origin}
        destination={destination}
        travelEndDate={travelEndDateTime}
        travelStartDate={travelStartDateTime}
        isOneWay={isOneWay}
        onClick={() => handleUpcomingTripOnClick(booking.productId)}
      />
    )
  }
  if (booking.productId === 5) {
    const { hotelName } = booking as HotelBooking | BookedHotelApiResponse
    return (
      <UpcomingHotel
        hotelName={hotelName}
        destination={destination}
        travelEndDate={travelEndDateTime}
        travelStartDate={travelStartDateTime}
        truncateHeading
        onClick={() => handleUpcomingTripOnClick(booking.productId)}
      />
    )
  }
  if (booking.productId === 8) {
    return (
      <UpcomingRentalCar
        rentalCarName="Your Upcoming Rental Car"
        destination={destination}
        travelEndDate={travelEndDateTime}
        travelStartDate={travelStartDateTime}
        onClick={() => handleUpcomingTripOnClick(booking.productId)}
        truncateHeading
      />
    )
  }
}

const getProductTypeById = (productId: 1 | 5 | 8) => {
  switch (productId) {
    case 1:
      return 'FLY'
    case 5:
      return 'STAY'
    case 8:
      return 'DRIVE'
    // no default
  }
}

function UpcomingTrip({
  booking,
  xSellUrl,
  xSellTargetId
}: {
  booking: Booking | BookedItemApiResponse
  xSellUrl: string
  xSellTargetId: ProductType
}) {
  useEffect(() => {
    fireViewPromotionXsell(booking.productId, booking.offerId)
  }, [booking])

  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme.breakpoints.lg as string})`
  })

  const crossSellProduct = getProductTypeById(xSellTargetId)
  const primaryProduct = getProductTypeById(booking.productId)

  const crossSellOnClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation()
      fireViewPromotionClick(booking.productId, xSellTargetId, booking.offerId)
      window.open(xSellUrl)
    },
    [booking, xSellTargetId, xSellUrl]
  )

  const isBrowser = useDetectBrowser()

  return (
    isBrowser && (
      <Relative mt={2}>
        <Flex justifyContent="space-between" px={1}>
          <Heading
            textStyle={['heading4', null, null, 'heading3']}
            color="text.heading"
            m={0}
            mb={3}
            as="h1"
          >
            Your Upcoming Trip
          </Heading>
          {isDesktop && (
            <Box>
              <ViewMyTrips />
            </Box>
          )}
        </Flex>
        <Flex flexDirection={['column', null, null, 'row']}>
          <Box width={[1, null, null, 1 / 2]} px={1} mb={[2, null, null, 0]}>
            <UpcomingTripCard booking={booking} />
          </Box>
          <Box width={[1, null, null, 1 / 2]} px={1} mb={[2, null, null, 0]}>
            {crossSellProduct && primaryProduct && (
              <CrossSellCard
                crossSellProduct={crossSellProduct}
                primaryProduct={primaryProduct}
                onClick={crossSellOnClick}
              />
            )}
          </Box>
          {!isDesktop && (
            <Flex flexDirection="column" px={2} mt={2} mb={4}>
              <ViewMyTrips />
            </Flex>
          )}
        </Flex>
      </Relative>
    )
  )
}

export default UpcomingTrip
